import React, { Suspense, useEffect, useState } from 'react';
import { AR } from '@one-thd/sui-icons';
import { mediaGalleryHorizontalPropTypes } from './propTypes';
import Thumbnails from './subcomponents/Thumbnails';
import MainImage from './subcomponents/MainImage';
import FloatingActionButton from '../common/FloatingActionButton';
import {
  handleHideOverlay,
  handleMainImageClick,
  handleThumbnailEvent,
  organizeThumbnails
} from './utils/mediaGalleryUtils';
import {
  MEDIA_GALLERY_CONTAINER_HEIGHT,
  MEDIA_GALLERY_CONTAINER_MAXHEIGHT,
  MEDIA_GALLERY_CONTAINER_MAXWIDTH,
  MEDIA_GALLERY_THUMBNAILS_WIDTH
} from './constants';
import { mediaClick } from '../../publisher';

const MediaGalleryOverlayTabs = React.lazy(() => import(
  /* webpackChunkName: "media-gallery-overlay-tabs" */'./MediaGalleryOverlayTabs')
);

const MediaGalleryHorizontal = ({
  itemId = null,
  collection,
  media,
  overrideData = null,
  hideProductImagesLabel = false,
  product,
  configuredProductLabel,
  showAvataarAR,
  showAvataarDefault,
  augmentedRealityIndex,
  totalThumbnailsToShow,
  onAvataarButtonClick,
  mainImageFetchPriority = 'auto',
  showFloatingActionButton,
  isOverlayOnClickEnabled,
  noPipSpacing = false
}) => {

  const [mainImage, setMainImage] = useState(null);
  const [overlayOpened, setOverlayOpened] = useState(false);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  const [overlayInitialType, setOverlayInitialType] = useState('IMAGE');
  const [overlayInitialId, setOverlayInitialId] = useState(null);
  const [overlaySelectedIndex, setOverlaySelectedIndex] = useState(0);
  const [originalThumbnails, setOriginalThumbnails] = useState([]);
  const [originalMainImages, setOriginalMainImages] = useState([]);

  useEffect(() => {
    const { thumbnails: mediaThumbnails = [], main: mainImages = [] } = media;

    setOriginalThumbnails(mediaThumbnails);
    setOriginalMainImages(mainImages);

    if (showAvataarAR) {
      setSelectedThumbnailIndex(augmentedRealityIndex);
      setOverlayOpened(true);
    }
  }, [media, showAvataarAR, augmentedRealityIndex]);

  useEffect(() => {
    setSelectedThumbnailIndex(0);
    setMainImage(null);
    setOverlaySelectedIndex(0);
    setOverlayInitialType('IMAGE');
    setOverlayInitialId(null);
    setOverlayOpened(false);
  }, [itemId]);

  const organizedThumbs = organizeThumbnails(originalThumbnails, totalThumbnailsToShow);
  const organizedMedia = organizeThumbnails(originalMainImages, totalThumbnailsToShow);
  const isMobile = false;

  const containerClassName = noPipSpacing
    ? 'mediagallery sui-flex sui-flex-col sui-gap-6 sui-w-full'
    : 'mediagallery sui-flex sui-flex-col sui-gap-6 sui-w-full sui-mx-auto';
  const containerStyle = noPipSpacing
    ? undefined
    : { maxWidth: MEDIA_GALLERY_CONTAINER_MAXWIDTH };
  const thumbnailsStyle = noPipSpacing
    ? undefined
    : { maxWidth: MEDIA_GALLERY_THUMBNAILS_WIDTH };

  return (
    <>
      <div
        className={containerClassName}
        style={containerStyle}
        data-component="MediaGalleryHorizontal"
        data-testid="MediaGalleryHorizontal"
      >
        <div
          className="sui-flex sui-items-center sui-justify-center sui-w-full sui-mx-auto sui-relative"
          style={{
            height: MEDIA_GALLERY_CONTAINER_HEIGHT,
            maxHeight: MEDIA_GALLERY_CONTAINER_MAXHEIGHT
          }}
        >
          {showFloatingActionButton && (
            <FloatingActionButton
              itemId={itemId || product?.itemId}
              icon={AR}
              isMobile={isMobile}
            />
          )}
          <MainImage
            mainImage={mainImage}
            selectedThumbnailIndex={selectedThumbnailIndex}
            media={media}
            identifiers={product?.identifiers}
            mainImageFetchPriority={mainImageFetchPriority}
            overrideData={overrideData}
            showAvataarDefault={showAvataarDefault}
            onMainImageClick={(thumbnailType) => {
              handleMainImageClick({
                thumbnailType,
                augmentedRealityIndex,
                selectedThumbnailIndex,
                mainImage,
                setOverlayOpened,
                organizedThumbs,
                originalThumbnails,
                setOverlaySelectedIndex,
                setOverlayInitialType,
                setOverlayInitialId,
                mediaClick,
                setSelectedThumbnailIndex
              });
            }}
          />
        </div>
        <div className="sui-mt-4" style={thumbnailsStyle}>
          <Thumbnails
            thumbnailList={organizedThumbs}
            mainImageList={organizedMedia}
            selectedThumbnailIndex={selectedThumbnailIndex}
            totalThumbnailsToShow={totalThumbnailsToShow}
            originalThumbnailCount={originalThumbnails.length}
            noPipSpacing={noPipSpacing}
            onThumbnailEvent={(thumbnailList, images, image, thumbnail, eventType) => {
              return handleThumbnailEvent({
                thumbnailList: organizedThumbs,
                images: organizedMedia,
                thumbnail,
                eventType,
                totalThumbnailsToShow,
                originalThumbnailCount: originalThumbnails.length,
                mainImage,
                setSelectedThumbnailIndex,
                setMainImage,
                setThumbnails: setOriginalThumbnails,
                setOverlayOpened,
                setOverlayInitialType,
                setOverlayInitialId,
                augmentedRealityIndex,
                originalMainImages,
                isOverlayOnClickEnabled
              });
            }}
          />
        </div>
      </div>
      {
        overlayOpened && (
          <Suspense fallback={<div />}>
            <MediaGalleryOverlayTabs
              handleClose={() => handleHideOverlay(setOverlayOpened, onAvataarButtonClick)}
              overlayOpened={overlayOpened}
              collection={collection}
              media={media}
              product={product}
              mainAsset={mainImage}
              selectedImageIndex={overlaySelectedIndex}
              thumbnails={originalThumbnails}
              overrideData={overrideData}
              hideProductImagesLabel={hideProductImagesLabel}
              configuredProductLabel={configuredProductLabel}
              videoAnalyticsData={{ position: (overlaySelectedIndex + 1).toString() }}
              itemId={product?.itemId || ''}
              showAvataarDefault={showAvataarDefault}
              showAvataarAR={showAvataarAR}
              initialType={overlayInitialType}
              initialId={overlayInitialId}
            />
          </Suspense>
        )
      }
    </>
  );
};

MediaGalleryHorizontal.propTypes = mediaGalleryHorizontalPropTypes;

export default MediaGalleryHorizontal;
